import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { HTTPService } from "../../../services/httpService";

const API = new HTTPService();

export class TemplateModal extends Component {
    state = {
        templateName: "",
        selectedServices: new Set(),
        activeTab: "",
        serviceCategories: [],
        loading: false,
        errors: {},
        saving: false
    };

    async componentDidMount() {
        await this.loadServiceCatalog();
    }

    componentDidUpdate(prevProps) {
        // Reset form when modal opens/closes or action changes
        if (this.props.show && !prevProps.show) {
            this.resetForm();
        }

        // Load template data when editing
        if (this.props.action === "edit" && this.props.template !== prevProps.template) {
            this.loadTemplateData();
        }
    }

    // Load service catalog from API
    async loadServiceCatalog() {
        try {
            this.setState({ loading: true });
            const response = await API.get(`/services/catalog/${this.props.typeId}`);
            
            //console.log('🔍 Full API response:', response);
            //console.log('📊 Response data:', response.data);
            
            // Handle different response formats
            let services;
            if (response.data && response.data.data) {
                // Format: { success: true, data: [...] }
                services = response.data.data;
            } else if (response.data && Array.isArray(response.data)) {
                // Format: [...]
                services = response.data;
            } else if (Array.isArray(response.data)) {
                // Direct array
                services = response.data;
            } else {
                console.error('❌ Unexpected response format:', response.data);
                throw new Error('Invalid response format from service catalog API');
            }
            
            //console.log('📋 Processed services:', services);
            //console.log('🔢 Number of services:', services?.length || 0);
            
            // Check for duplicate master_service_ids
            const serviceIdCounts = {};
            services.forEach(service => {
                const id = service.master_service_id;
                serviceIdCounts[id] = (serviceIdCounts[id] || 0) + 1;
            });
            
            const duplicateIds = Object.keys(serviceIdCounts).filter(id => serviceIdCounts[id] > 1);
            if (duplicateIds.length > 0) {
                console.warn('⚠️ Duplicate master_service_ids found:', duplicateIds);
                console.warn('⚠️ ID counts:', Object.fromEntries(
                    duplicateIds.map(id => [id, serviceIdCounts[id]])
                ));
            } else {
                //console.log('✅ No duplicate master_service_ids found');
            }
            
            if (!Array.isArray(services)) {
                console.error('❌ Services is not an array:', typeof services, services);
                throw new Error('Services data is not an array');
            }

            // First, deduplicate services by master_service_id and filter out "Custom" services
            const uniqueServicesMap = new Map();
            let customServicesFiltered = 0;
            
            services.forEach(service => {
                const id = service.master_service_id;
                const description = service.description || '';
                
                // Skip services with "Custom" in the description
                if (description.toLowerCase().includes('custom')) {
                    customServicesFiltered++;
                    //console.log('🚫 Filtering out custom service:', id, description);
                    return;
                }
                
                if (!uniqueServicesMap.has(id)) {
                    uniqueServicesMap.set(id, service);
                } else {
                    //console.log('🔄 Skipping duplicate service:', id, service.description);
                }
            });
            
            const uniqueServices = Array.from(uniqueServicesMap.values());
            //console.log('📊 Original services:', services.length);
            //console.log('🚫 Custom services filtered:', customServicesFiltered);
            //console.log('✅ Final unique services:', uniqueServices.length);

            // Group unique services by category
            const categoriesMap = {};
            uniqueServices.forEach(service => {
                if (!categoriesMap[service.service_category]) {
                    categoriesMap[service.service_category] = {
                        category_name: service.service_category,
                        services: []
                    };
                }
                
                categoriesMap[service.service_category].services.push({
                    master_service_id: service.master_service_id,
                    description: service.description
                });
            });

            const serviceCategories = Object.values(categoriesMap);
            
            //console.log('🏷️ Service categories created:', serviceCategories);
            
            this.setState({ 
                serviceCategories,
                activeTab: serviceCategories[0]?.category_name || "",
                loading: false 
            });
            
        } catch (error) {
            console.error("Error loading service catalog:", error);
            console.error("Error details:", error.response?.data);
            
            // For now, show error instead of loading mock data
            this.setState({ 
                serviceCategories: [],
                loading: false,
                errors: { ...this.state.errors, loadError: 'Failed to load service catalog' }
            });
            
            // Load mock data as fallback
            this.loadMockServiceData();
        }
    }

    // Mock data for development (remove when API is ready)
    loadMockServiceData() {
        const mockCategories = [
            {
                category_name: "Ceramic Coating",
                services: [
                    { master_service_id: 1, description: "Basic Ceramic Coating" },
                    { master_service_id: 2, description: "Premium Ceramic Coating" },
                    { master_service_id: 3, description: "Ceramic Coating - Full Vehicle" },
                    { master_service_id: 4, description: "Ceramic Coating - Partial" }
                ]
            },
            {
                category_name: "PPF",
                services: [
                    { master_service_id: 5, description: "PPF Full Vehicle" },
                    { master_service_id: 6, description: "PPF Front End" },
                    { master_service_id: 7, description: "PPF Hood" },
                    { master_service_id: 8, description: "PPF Bumper" }
                ]
            },
            {
                category_name: "Window Tint",
                services: [
                    { master_service_id: 54, description: "Front Doors Tint" },
                    { master_service_id: 56, description: "Rear Doors Tint" },
                    { master_service_id: 58, description: "Windshield Tint" }
                ]
            },
            {
                category_name: "Vinyl Wrap",
                services: [
                    { master_service_id: 10, description: "Full Vehicle Wrap" },
                    { master_service_id: 11, description: "Partial Wrap" },
                    { master_service_id: 12, description: "Racing Stripes" }
                ]
            },
            {
                category_name: "Other Services",
                services: [
                    { master_service_id: 13, description: "Chrome Delete" },
                    { master_service_id: 14, description: "Paint Correction" },
                    { master_service_id: 15, description: "Interior Detail" }
                ]
            }
        ];

        //console.log('📝 Loading mock service data as fallback');
        this.setState({ 
            serviceCategories: mockCategories,
            activeTab: mockCategories[0]?.category_name || "",
            loading: false 
        });
    }

    // Reset form to initial state
    resetForm() {
        this.setState({
            templateName: "",
            selectedServices: new Set(),
            activeTab: this.state.serviceCategories[0]?.category_name || "",
            errors: {}
        });
    }

    // Load template data for editing
    loadTemplateData() {
        if (this.props.template && this.props.action === "edit") {
            const { template } = this.props;
            
            // Load existing template services (they come from the detailed template data)
            const selectedServices = new Set();
            if (template.services && Array.isArray(template.services)) {
                template.services.forEach(service => {
                    selectedServices.add(service.master_service_id);
                });
            }

            this.setState({
                templateName: template.template_name || "",
                selectedServices
            });
        }
    }

    // Handle template name change
    handleNameChange = (event) => {
        this.setState({ 
            templateName: event.target.value,
            errors: { ...this.state.errors, templateName: "" }
        });
    };

    // Handle service selection toggle
    handleServiceToggle = (serviceId) => {
        const newSelected = new Set(this.state.selectedServices);
        if (newSelected.has(serviceId)) {
            newSelected.delete(serviceId);
        } else {
            newSelected.add(serviceId);
        }
        this.setState({ 
            selectedServices: newSelected,
            errors: { ...this.state.errors, services: "" }
        });
    };

    // Handle tab change
    handleTabChange = (categoryName) => {
        this.setState({ activeTab: categoryName });
    };

    // Validate form
    validateForm() {
        const errors = {};

        if (!this.state.templateName.trim()) {
            errors.templateName = "Template name is required";
        }

        if (this.state.selectedServices.size === 0) {
            errors.services = "At least one service must be selected";
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    // Handle form submission
    handleSubmit = async () => {
        if (!this.validateForm()) {
            return;
        }

        this.setState({ saving: true });

        try {
            // Ensure we're sending the correct field name
            const templateData = {
                template_name: this.state.templateName.trim(),
                services: Array.from(this.state.selectedServices),
                type_id: parseInt(this.props.typeId) // Use type_id to match what backend expects
            };

            //console.log('Frontend sending template data:', templateData);

            if (this.props.action === "edit") {
                // Update existing template
                const response = await API.put(`/templates/${this.props.typeId}/${this.props.template.template_id}`, templateData);
                //console.log('Template update response:', response);
            } else {
                // Create new template
                const response = await API.post('/templates', templateData);
                //console.log('Template creation response:', response);
            }

            // Close modal and refresh parent data
            this.props.onHide();
            if (this.props.onSave) {
                this.props.onSave();
            }

        } catch (error) {
            console.error("Error saving template:", error);
            console.error("Error response:", error.response?.data);
            
            // Handle specific error responses
            let errorMessage = "Failed to save template. Please try again.";
            if (error.response && error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
            }
            
            this.setState({ 
                errors: { 
                    submit: errorMessage
                }
            });
        } finally {
            this.setState({ saving: false });
        }
    };

    // Get selected count for a category
    getSelectedCountForCategory(category) {
        return category.services.filter(service => 
            this.state.selectedServices.has(service.master_service_id)
        ).length;
    }

    render() {
        const { show, onHide, action } = this.props;
        const { 
            templateName, 
            selectedServices, 
            activeTab, 
            serviceCategories, 
            loading, 
            errors, 
            saving 
        } = this.state;

        const isEditing = action === "edit";
        const totalSelected = selectedServices.size;

        return (
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa' }}>
                    <Modal.Title>
                        {isEditing ? 'Edit Template' : 'Create New Template'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: 0 }}>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading service catalog...</p>
                        </div>
                    ) : (
                        <>
                            {/* Template Name Section */}
                            <div className="px-4 py-3 border-bottom bg-light">
                                <Form.Group>
                                    <Form.Label className="font-weight-bold">Template Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter template name..."
                                        value={templateName}
                                        onChange={this.handleNameChange}
                                        isInvalid={!!errors.templateName}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.templateName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>

                            {/* Service Selection Section */}
                            <div className="px-4 py-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0 font-weight-bold">Select Services</h6>
                                    <small className="text-muted">
                                        {totalSelected} service{totalSelected !== 1 ? 's' : ''} selected
                                    </small>
                                </div>

                                {errors.services && (
                                    <div className="alert alert-danger py-2 mb-3">
                                        {errors.services}
                                    </div>
                                )}

                                {errors.loadError && (
                                    <div className="alert alert-warning py-2 mb-3">
                                        {errors.loadError} - Using sample data for demonstration.
                                    </div>
                                )}

                                {/* Category Tabs */}
                                <div style={{ height: '400px' }}>
                                    {serviceCategories.length > 0 ? (
                                        <>
                                            <div className="border-bottom mb-3">
                                                <nav className="nav" style={{ flexWrap: 'nowrap', overflowX: 'auto', borderBottom: '1px solid #dee2e6' }}>
                                                    {serviceCategories.map(category => {
                                                        const categorySelectedCount = this.getSelectedCountForCategory(category);
                                                        const isActive = activeTab === category.category_name;
                                                        
                                                        return (
                                                            <button
                                                                key={category.category_name}
                                                                onClick={() => this.handleTabChange(category.category_name)}
                                                                className="nav-link px-3 py-2"
                                                                style={{ 
                                                                    whiteSpace: 'nowrap', 
                                                                    border: 'none',
                                                                    backgroundColor: 'transparent',
                                                                    color: isActive ? '#007bff' : '#6c757d',
                                                                    fontWeight: isActive ? '600' : '400',
                                                                    position: 'relative',
                                                                    borderBottom: isActive ? '3px solid #007bff' : '3px solid transparent',
                                                                    transition: 'all 0.2s ease-in-out',
                                                                    cursor: 'pointer',
                                                                    outline: 'none',
                                                                    marginBottom: '-1px' // To overlap the border-bottom of the container
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!isActive) {
                                                                        e.target.style.color = '#495057';
                                                                        e.target.style.borderBottomColor = '#dee2e6';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!isActive) {
                                                                        e.target.style.color = '#6c757d';
                                                                        e.target.style.borderBottomColor = 'transparent';
                                                                    }
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <span>{category.category_name}</span>
                                                                    {categorySelectedCount > 0 && (
                                                                        <span 
                                                                            className="badge ml-2" 
                                                                            style={{ 
                                                                                fontSize: '10px',
                                                                                backgroundColor: isActive ? '#007bff' : '#6c757d',
                                                                                color: 'white'
                                                                            }}
                                                                        >
                                                                            {categorySelectedCount}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </nav>
                                            </div>

                                            {/* Tab Content */}
                                            <div style={{ height: '320px', overflowY: 'auto' }}>
                                                {serviceCategories.map(category => (
                                                    activeTab === category.category_name && (
                                                        <div key={category.category_name}>
                                                            {category.services.map(service => (
                                                                <div
                                                                    key={service.master_service_id}
                                                                    className="d-flex align-items-center p-3 mb-2"
                                                                    style={{ 
                                                                        border: '1px solid #e9ecef', 
                                                                        borderRadius: '6px',
                                                                        backgroundColor: selectedServices.has(service.master_service_id) ? '#e3f2fd' : '#ffffff',
                                                                        borderColor: selectedServices.has(service.master_service_id) ? '#2196f3' : '#e9ecef',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease'
                                                                    }}
                                                                    onClick={() => this.handleServiceToggle(service.master_service_id)}
                                                                    onMouseEnter={(e) => {
                                                                        if (!selectedServices.has(service.master_service_id)) {
                                                                            e.target.style.backgroundColor = '#f5f5f5';
                                                                        }
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        if (!selectedServices.has(service.master_service_id)) {
                                                                            e.target.style.backgroundColor = '#ffffff';
                                                                        }
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedServices.has(service.master_service_id)}
                                                                        onChange={() => this.handleServiceToggle(service.master_service_id)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        style={{ 
                                                                            marginRight: '12px',
                                                                            transform: 'scale(1.2)'
                                                                        }}
                                                                    />
                                                                    <label
                                                                        style={{ 
                                                                            cursor: 'pointer', 
                                                                            userSelect: 'none',
                                                                            margin: 0,
                                                                            fontWeight: selectedServices.has(service.master_service_id) ? '500' : '400',
                                                                            color: selectedServices.has(service.master_service_id) ? '#1976d2' : '#333'
                                                                        }}
                                                                    >
                                                                        {service.description}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-muted" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">No services available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ backgroundColor: '#f8f9fa' }}>
                    {errors.submit && (
                        <div className="alert alert-danger py-2 mb-2 w-100">
                            {errors.submit}
                        </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <small className="text-muted">
                            {totalSelected} of {serviceCategories.reduce((acc, cat) => acc + cat.services.length, 0)} services selected
                        </small>
                        <div>
                            <Button variant="secondary" onClick={onHide} disabled={saving}>
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={this.handleSubmit} 
                                disabled={saving || loading || serviceCategories.length === 0}
                                className="ml-2"
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                        {isEditing ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    isEditing ? 'Update Template' : 'Create Template'
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default TemplateModal;